import React from 'react';
import c from 'classnames';
import config from 'react-global-configuration';
import { useDispatch } from 'react-redux';
import { history } from 'instantsearch.js/es/lib/routers';
import { useSearchBox } from 'react-instantsearch';

import { SearchkitWrapper } from '@uncinc/react-searchkit';
import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import {
  SEARCH_PATH,
  SEARCHKIT_HOST_URL,
  SEARCHKIT_INDEX_ALL_CONTENT,
} from '../../config/constants';

import {
  searchAttributes,
  resultAttributes,
  facetAttributes,
} from './config';

import { setSearchParams } from '../../store/actions/UI';

// Subcomponents.
import Autocomplete from './Autocomplete';
import Facets from './Facets';
import Results from './Results';
import SearchBar from './SearchBar';

interface SearchAllContentSubComponents {
  Autocomplete: typeof Autocomplete;
  Facets: typeof Facets;
  Results: typeof Results;
  SearchBar: typeof SearchBar;
}

interface RenderProps {
  refine: (query: string) => void;
}

interface InnerProps {
  children: React.ReactNode | ((renderProps: RenderProps) => React.ReactNode);
}

export type SearchAllContentProps = Omit<
SearchkitWrapperProps,
'connection' | 'indexName' | 'routing' | 'searchSettings' | 'children'
> & InnerProps;

const Inner: React.FC<InnerProps> = ({ children }) => {
  const { refine } = useSearchBox();

  if (typeof children === 'function') {
    return children({ refine });
  }

  return children;
};

// eslint-disable-next-line max-len
const SearchAllContent: React.FC<SearchAllContentProps> & SearchAllContentSubComponents = (props) => {
  const {
    className,
    children,
    ...restProps
  } = props;

  const dispatch = useDispatch();

  const { pathname } = window.location;

  const classNames = c('SearchAllContent', className);

  return (
    <SearchkitWrapper
      {...restProps}
      className={classNames}
      onlyFetchWithQuery={pathname !== SEARCH_PATH}
      connection={{ host: SEARCHKIT_HOST_URL }}
      indexName={SEARCHKIT_INDEX_ALL_CONTENT}
      onStateChange={({
        uiState,
        setUiState,
      }) => {
        dispatch(setSearchParams(uiState[SEARCHKIT_INDEX_ALL_CONTENT]?.query));
        setUiState(uiState);
      }}
      routing={{
        router: history({
          writeDelay: 0,
        }),
      }}
      searchSettings={{
        search_attributes: searchAttributes,
        result_attributes: resultAttributes,
        facet_attributes: facetAttributes,
        highlight_attributes: ['title', 'summary', 'description'],
        sorting: {
          default: [{ field: '_score', order: 'desc' }, { field: 'entity_type', order: 'desc' }],
        },
      }}
      requestOptions={{
        // @ts-expect-error A 'must' query if not part of the QueryDslQueryContainer
        // typing, but the query is valid nonetheless. This should be fixed in
        // @elastic/elasticsearch/lib/api/types.
        getBaseFilters: () => [
          {
            bool: {
              must: {
                term: {
                  field_domain_access: {
                    value: config.get('CURRENT_DOMAIN_DRUPAL_IDENTIFIER') as string,
                  },
                },
              },
            },
          },
        ],
      }}
    >
      <Inner>
        {children}
      </Inner>
    </SearchkitWrapper>
  );
};

SearchAllContent.Autocomplete = Autocomplete;
SearchAllContent.Facets = Facets;
SearchAllContent.Results = Results;
SearchAllContent.SearchBar = SearchBar;

export default SearchAllContent;
